import type { ReactNode } from "react";
import { Button, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ArrowLeft24Regular, Home24Regular } from "@fluentui/react-icons";
import { navigate } from "../lib/nav";

const useStyles = makeStyles({
  root: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minHeight: "56px",
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  title: {
    flexGrow: 1,
    minWidth: 0,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  main: {
    flexGrow: 1,
    width: "100%",
    maxWidth: "1240px",
    marginLeft: "auto",
    marginRight: "auto",
    boxSizing: "border-box",
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    "@media (max-width: 600px)": { padding: tokens.spacingHorizontalM },
  },
  footer: {
    position: "sticky",
    bottom: 0,
    zIndex: 10,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: tokens.spacingHorizontalM,
    paddingBottom: `calc(${tokens.spacingHorizontalM} + env(safe-area-inset-bottom))`,
  },
  footerInner: {
    width: "100%",
    maxWidth: "1240px",
    boxSizing: "border-box",
    marginLeft: "auto",
    marginRight: "auto",
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
  },
});

/**
 * Struttura di pagina: intestazione fissa in alto (con il ritorno al menu), contenuto
 * scorrevole fino a 1240 px, barra azioni fissa in basso allineata a destra.
 */
export function Shell(props: {
  title: ReactNode;
  onBack?: () => void;
  actions?: ReactNode;
  footer?: ReactNode;
  /** false per nascondere il pulsante del menu principale (es. nella schermata di benvenuto). */
  home?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const s = useStyles();
  return (
    <div className={s.root}>
      <header className={s.header}>
        {props.onBack && (
          <Button appearance="subtle" icon={<ArrowLeft24Regular />} aria-label="Indietro" onClick={props.onBack} />
        )}
        {props.home !== false && (
          <Button appearance="subtle" icon={<Home24Regular />} aria-label="Menu principale" title="Menu principale" onClick={() => navigate("/")} />
        )}
        <div className={s.title}>{props.title}</div>
        {props.actions}
      </header>
      <main className={mergeClasses(s.main, props.className)}>{props.children}</main>
      {props.footer && (
        <footer className={s.footer}>
          <div className={s.footerInner}>{props.footer}</div>
        </footer>
      )}
    </div>
  );
}
